import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

/**
 * Edit a thread's name AND its accent colour. The accent drives both the
 * sidebar avatar and the chat-bubble tint, so changing it instantly re-themes
 * every note inside the thread.
 */
export default function RenameDialog({ title, onClose, onSubmit, colors }) {
  const palette = colors || ["#2563EB", "#8B5CF6", "#EC4899", "#F59E0B", "#10B981", "#EF4444", "#06B6D4"];
  const [name, setName] = useState("");
  const [color, setColor] = useState(palette[0]);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (title) {
      setName(title.name || "");
      setColor(title.color || palette[0]);
      setCustom("");
    }
  }, [title]);

  const handle = async (e) => {
    e?.preventDefault?.();
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      await onSubmit({ name: name.trim(), color });
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const applyCustom = (v) => {
    setCustom(v);
    // Accept only valid 6-digit hex before swapping the live preview.
    if (/^#[0-9a-fA-F]{6}$/.test(v)) setColor(v);
  };

  return (
    <Dialog open={!!title} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-[#27272A] text-[#F3F4F6]">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit title</DialogTitle>
        </DialogHeader>
        <form onSubmit={handle} className="space-y-5">
          <div className="space-y-2">
            <Label className="text-xs text-[#9CA3AF]">Name</Label>
            <Input
              autoFocus
              data-testid="rename-title-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-[#1E1E1E] border-[#27272A] rounded-xl h-11"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-[#9CA3AF]">Bubble color</Label>
            <div className="flex items-center gap-2 flex-wrap">
              {palette.map((c) => (
                <button
                  key={c}
                  type="button"
                  data-testid={`color-swatch-${c}`}
                  onClick={() => { setColor(c); setCustom(""); }}
                  className={`h-8 w-8 rounded-full transition-all ${
                    color === c ? "ring-2 ring-offset-2 ring-offset-[#121212] ring-[#F3F4F6] scale-110" : "hover:scale-105"
                  }`}
                  style={{ backgroundColor: c }}
                  aria-label={`Color ${c}`}
                />
              ))}
              <label
                className="h-8 w-8 rounded-full relative cursor-pointer border border-[#27272A] overflow-hidden"
                title="Pick a custom color"
                style={{ background: "conic-gradient(red, yellow, lime, cyan, blue, magenta, red)" }}
              >
                <input
                  type="color"
                  data-testid="color-swatch-custom"
                  value={color.startsWith("#") && color.length === 7 ? color : "#2563EB"}
                  onChange={(e) => applyCustom(e.target.value)}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
              </label>
            </div>
            {/* Live preview so users see what the new bubble looks like. */}
            <div className="flex justify-end pt-1">
              <div
                data-testid="bubble-preview"
                className="text-white rounded-2xl rounded-br-md px-4 py-2 text-sm shadow-sm"
                style={{ backgroundColor: color }}
              >
                Preview
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              onClick={onClose}
              variant="ghost"
              className="rounded-full text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#1E1E1E]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="rename-title-submit-btn"
              disabled={!name.trim() || busy}
              className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            >
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
